import React, { useEffect, useState, useCallback } from 'react';
import { getDayOfTheWeek, dateAsString } from '../../utils/dates';
import { twelveHourClockTo24 } from '../opening-times/helpers';
import { sendEvent } from '../../utils/analytics';
import { isBrowser } from '../../utils/is-browser';

import { useBankHolidays } from './use-bankholidays';
import { useWebchatConfig } from './use-webchatconfig';

export const SNAPENGAGE_READY_EVENT = 'snapengage-ready';
export const statuses = {
  ONLINE: 'Online',
  OFFLINE: 'Offline',
  BUSY: 'Busy',
  BANK_HOLIDAY: 'BankHoliday',
  LOADING: 'Loading',
};

export const isBankHoliday = (dateTime, bh) => {
  const formattedDate = dateAsString(dateTime, 'YYYY-MM-DD');
  const division =
    process.env.SHELTER_SITE === 'scotland' ? 'scotland' : 'england_and_wales';
  return bh[division].events.some((d) => d.date === formattedDate);
};

const isWebchatServiceAvailable = (dateTime, config) => {
  const dayName = getDayOfTheWeek(dateTime).toLowerCase();

  const allServiceHours = JSON.parse(config.serviceHours.internal.content);
  const serviceHours = allServiceHours[dayName];

  if (serviceHours.closed) return false;

  const hour = dateTime.getHours();
  const opens = twelveHourClockTo24(
    serviceHours.opens,
    serviceHours.opens_ampm
  );

  const closes = twelveHourClockTo24(
    serviceHours.closes,
    serviceHours.closes_ampm
  );

  return hour >= opens && hour < closes;
};

const getStatus = (agentStatus, config, bh) => {
  const now = new Date(Date.now());

  if (agentStatus) {
    return statuses.ONLINE;
  }
  if (isBankHoliday(now, bh)) {
    return statuses.BANK_HOLIDAY;
  }
  if (!isWebchatServiceAvailable(now, config)) {
    return statuses.OFFLINE;
  }
  return statuses.BUSY;
};

export const startChat = () =>
  isBrowser && window.SnapEngage && window.SnapEngage.startLink();

const handlers = [];
let state;

// testing utility
export const reset = () => {
  state = undefined;
};

if (isBrowser) {
  window.addEventListener(SNAPENGAGE_READY_EVENT, () => {
    /**
     * Register analytics events
     * Note: Further status events are fired in the widget
     */
    window.SnapEngage.setCallback('Open', () =>
      sendEvent({
        event: 'SnapEngage',
        eventAction: 'Chat Form Opened',
        eventLabel: 'Online',
      })
    );
    window.SnapEngage.setCallback('StartChat', () =>
      sendEvent({
        event: 'SnapEngage',
        eventAction: 'User Initiated Chat',
        eventLabel: 'Manual',
      })
    );

    window.SnapEngage.getAgentStatusAsync((isAgentOnline) => {
      state = isAgentOnline;
      handlers.forEach((handler) => handler(isAgentOnline));
      handlers.splice(0, handlers.length);
    });
  });
}

// eslint-disable-next-line react/display-name
export const withChatStatus = (WrappedComponent) => (props) => {
  // Temporary way to use the professionals advice config oo the professionals advice page
  const config = useWebchatConfig();

  const bankhols = useBankHolidays();

  const [status, setStatus] = useState(
    state === undefined ? statuses.LOADING : getStatus(state, config, bankhols)
  );

  const handler = useCallback(
    (isAgentOnline) => setStatus(getStatus(isAgentOnline, config, bankhols)),
    [config, bankhols]
  );

  /**
   * Monitor the
   */
  useEffect(() => {
    switch (status) {
      case statuses.ONLINE:
        sendEvent({
          event: 'SnapEngage',
          eventAction: 'Chat availability',
          eventLabel: 'Available',
          componentType: WrappedComponent.displayName,
        });
        break;
      case statuses.OFFLINE:
        sendEvent({
          event: 'SnapEngage',
          eventAction: 'Chat availability',
          eventLabel: 'Unavailable',
          componentType: WrappedComponent.displayName,
        });
        break;
      case statuses.BUSY:
        sendEvent({
          event: 'SnapEngage',
          eventAction: 'Chat availability',
          eventLabel: 'Busy',
          componentType: WrappedComponent.displayName,
        });
        break;
      case statuses.BANK_HOLIDAY:
        sendEvent({
          event: 'SnapEngage',
          eventAction: 'Chat availability',
          eventLabel: 'Bank Holiday',
          componentType: WrappedComponent.displayName,
        });
        break;
      default:
        break;
    }
  }, [status]);

  useEffect(() => {
    if (state === undefined) {
      handlers.push(handler);
    }

    // cleanup
    return () => {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(handlers.indexOf(handler), 1);
      }
    };
  }, [handler]);

  return <WrappedComponent status={status} config={config} {...props} />;
};
